import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { buildApiRequest } from './authConfig';
import { SessionsResponse, ApiError } from '../types/session';
import { AppConfig, AvailableHostPool } from '../types/appConfig';
import { getApiBaseUrl, getPublicRuntimeConfig } from './runtimeConfig';

async function readJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }

  const body = await response.text();
  const preview = body.slice(0, 160).trim();

  throw new Error(
    preview.startsWith('<')
      ? 'The API returned HTML instead of JSON. Check VITE_API_BASE_URL and the frontend /api routing.'
      : `The API returned an unexpected response instead of JSON: ${preview || 'empty response'}`
  );
}

async function readApiError(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const error = (await response.json().catch(() => undefined)) as ApiError | undefined;
    if (error?.error) {
      return error.error;
    }
  }

  const body = await response.text().catch(() => '');
  const preview = body.slice(0, 160).trim();

  if (preview.startsWith('<')) {
    return 'The API returned HTML instead of JSON. Check VITE_API_BASE_URL and the frontend /api routing.';
  }

  return preview || `HTTP ${response.status}`;
}

async function getAccessToken(instance: IPublicClientApplication, accounts: AccountInfo[]): Promise<string> {
  if (accounts.length === 0) {
    throw new Error('No accounts found');
  }

  const runtimeConfig = getPublicRuntimeConfig();
  const clientId = runtimeConfig.frontend.azureClientId;
  const tenantId = runtimeConfig.frontend.azureTenantId;
  if (!tenantId) {
    throw new Error('No Entra tenant configured for API token acquisition');
  }

  const account = accounts.find(
    (candidate) => candidate.tenantId?.toLowerCase() === tenantId.toLowerCase()
  );
  const apiRequest = buildApiRequest(clientId, tenantId);

  if (account) {
    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed, acquiring token using popup', error);
    }
  }

  const response = await instance.acquireTokenPopup(apiRequest);
  return response.accessToken;
}

export function useApiClient() {
  const { instance, accounts } = useMsal();

  const fetchWithAuth = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
    const token = await getAccessToken(instance, accounts);
    const apiBaseUrl = getApiBaseUrl();

    const response = await fetch(`${apiBaseUrl}${url}`, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(await readApiError(response));
    }

    return readJsonResponse<T>(response);
  };

  return {
    getSessions: async (): Promise<SessionsResponse> => {
      return fetchWithAuth('/sessions');
    },

    terminateSession: async (sessionId: string): Promise<void> => {
      await fetchWithAuth('/sessions/terminate', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      });
    },

    getAdminConfig: async (): Promise<AppConfig> => {
      return fetchWithAuth('/portal-config');
    },

    updateAdminConfig: async (config: AppConfig): Promise<AppConfig> => {
      return fetchWithAuth('/portal-config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
    },

    getAvailableHostPools: async (): Promise<{ hostPools: AvailableHostPool[] }> => {
      return fetchWithAuth('/portal-config/available-host-pools');
    },
  };
}
